<template>
  <div class="sidebar" :class="{ collapsed: isCollapsed }">
    <div class="sidebar-header">
      <div class="sidebar-brand">
        <div class="brand-logo">
          <svg width="48" height="22" viewBox="0 0 72 33" fill="none">
            <ellipse cx="36.3" cy="9.9" rx="11.7" ry="2.5" class="logo__path"></ellipse>
            <g>
              <ellipse cx="36.3" cy="23.5" rx="19.6" ry="2.5" class="logo__path"></ellipse>
              <path d="M72,16.3c0,1.2-10.1,2.1-31.9,2.1S0.6,17.5,0.6,16.3c0-1.2,17.7-2.1,39.5-2.1S72,15.1,72,16.3z" class="logo__path"></path>
            </g>
          </svg>
          <span class="brand-text">Cloudsway</span>
        </div>
      </div>
    </div>
    
    <button class="sidebar-toggle" @click="toggle" :title="isCollapsed ? '展开侧边栏' : '收起侧边栏'">
      <span class="toggle-icon">{{ isCollapsed ? '>' : '☰' }}</span>
    </button>
    
    <nav class="sidebar-nav">
      <ul class="nav-list">
        <li class="nav-item" v-for="item in navItems" :key="item.name">
          <router-link 
            :to="item.path" 
            class="nav-link"
            :class="{ active: $route.path === item.path }"
          >
            <i :class="item.icon"></i>
            <span class="nav-text">{{ item.text }}</span>
          </router-link>
        </li>
      </ul>
    </nav>
    
    <!-- 用户信息区域 -->
    <div class="user-info" v-if="userInfo">
      <div class="user-avatar">
        <i class="fas fa-user"></i>
      </div>
      <div class="user-details">
        <div class="user-name">{{ userInfo.name }}</div>
        <div class="user-email">{{ userInfo.email }}</div>
      </div>
      <button class="logout-btn" @click="handleLogout" title="退出登录">
        <i class="fas fa-sign-out-alt"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

export default {
  name: 'Sidebar',
  setup() {
    const router = useRouter()
    const isCollapsed = ref(localStorage.getItem('sidebar-collapsed') === 'true')
    
    const navItems = [
      {
        name: 'search',
        path: '/search',
        text: 'Search Playground',
        icon: 'fas fa-search'
      },
      {
        name: 'subscription',
        path: '/subscription',
        text: 'Subscription & Usage',
        icon: 'fas fa-chart-bar'
      },
      {
        name: 'api-keys',
        path: '/api-keys',
        text: 'API Key Management',
        icon: 'fas fa-key'
      }
    ]

    // 用户信息
    const userInfo = computed(() => {
      const storedUserInfo = localStorage.getItem('userInfo')
      return storedUserInfo ? JSON.parse(storedUserInfo) : null
    })

    // API 基础URL配置
    const API_BASE_URL = process.env.NODE_ENV === 'development' ? '' : process.env.BACKEND_API_BASE_URL

    // 退出登录
    const handleLogout = async () => {
      if (confirm('确定要退出登录吗？')) {
        try {
          // 调用后端登出API
          await axios.post(`${API_BASE_URL}/api/logout`, {}, {
            withCredentials: true
          })
        } catch (error) {
          console.error('Logout API call failed:', error)
          // 即使API调用失败也继续本地登出
        }
        
        // 清除本地存储
        localStorage.removeItem('userInfo')
        localStorage.removeItem('userToken')
        
        // 清除cookie
        document.cookie = 'console_cs_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
        
        // 跳转到登录页面
        router.push('/login')
      }
    }

    const toggle = () => {
      isCollapsed.value = !isCollapsed.value
      localStorage.setItem('sidebar-collapsed', isCollapsed.value.toString())
    }

    // 监听键盘快捷键
    const handleKeydown = (e) => {
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault()
        toggle()
      }
    }

    // 更新主内容区域的margin
    const updateMainContent = () => {
      const mainContent = document.querySelector('.main-content')
      if (mainContent) {
        if (isCollapsed.value) {
          mainContent.classList.add('sidebar-collapsed')
        } else {
          mainContent.classList.remove('sidebar-collapsed')
        }
      }
    }

    watch(isCollapsed, () => {
      updateMainContent()
    })

    onMounted(() => {
      document.addEventListener('keydown', handleKeydown)
      updateMainContent()
    })

    return {
      isCollapsed,
      navItems,
      userInfo,
      handleLogout,
      toggle
    }
  }
}
</script>

<style lang="scss" scoped>
/* 侧边栏主体 */
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 280px;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  z-index: 1000;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* 收起状态 */
.sidebar.collapsed {
  width: 80px;
}

/* 侧边栏头部 */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  height: 70px;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  min-width: 0;
}

.sidebar.collapsed .sidebar-header {
  justify-content: center;
  padding: 16px 12px;
}

/* 品牌Logo */
.sidebar-brand .brand-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2563EB;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  min-width: 0;
}

.sidebar.collapsed .brand-logo {
  justify-content: center;
  gap: 0;
  width: 100%;
}

.sidebar-brand svg {
  width: 48px;
  height: 22px;
  flex-shrink: 0;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.sidebar.collapsed .sidebar-brand svg {
  width: 32px;
  height: 16px;
}

.sidebar-brand .brand-text {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 24px;
  font-weight: 500;
  color: #2563EB;
  margin: 0;
  transition: opacity 0.3s ease, visibility 0.3s ease, transform 0.3s ease;
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
}

/* 切换按钮 */
.sidebar-toggle {
  position: fixed;
  left: 280px;
  top: 20px;
  background: #EBF8FF;
  border: 1px solid #e5e7eb;
  border-radius: 0 4px 4px 0;
  font-size: 18px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
  z-index: 1002;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  visibility: visible;
}

.sidebar-toggle:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.sidebar.collapsed .sidebar-toggle {
  background: #FEF2F2;
  left: 80px;
}

/* 导航区域 */
.sidebar-nav {
  padding: 16px 0;
  overflow: hidden;
}

.nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-item {
  margin: 4px 16px;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.sidebar.collapsed .nav-item {
  margin: 8px 12px;
  display: flex;
  justify-content: center;
}

/* 导航链接 */
.nav-link {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: #6b7280;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  font-size: 14px;
  position: relative;
  min-width: 0;
}

.sidebar.collapsed .nav-link {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
  width: 48px;
  height: 48px;
  margin: 0 auto;
  border-radius: 8px;
  min-width: 48px;
}

/* 导航图标 */
.nav-link i {
  width: 20px;
  text-align: center;
  margin-right: 12px;
  font-size: 16px;
  flex-shrink: 0;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.sidebar.collapsed .nav-link i {
  margin-right: 0;
  font-size: 16px;
  width: 20px;
}

/* 导航文字 */
.nav-text {
  font-size: 14px;
  transition: opacity 0.3s ease, visibility 0.3s ease, transform 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
}

/* 隐藏文字内容 */
.sidebar.collapsed .brand-text,
.sidebar.collapsed .nav-text {
  opacity: 0;
  visibility: hidden;
  transform: translateX(-20px);
  width: 0;
}

/* 悬停效果 */
.nav-link:hover {
  background-color: #f3f4f6;
  color: #374151;
  font-size: 14px;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.sidebar.collapsed .nav-link:hover {
  font-size: 14px;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  background-color: #f3f4f6;
}

/* 激活状态 */
.nav-link.active {
  background-color: #eff6ff;
  color: #4285f4;
  font-weight: 500;
  font-size: 14px;
}

.sidebar.collapsed .nav-link.active {
  background-color: #eff6ff;
  color: #4285f4;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.2);
}

/* Logo图标样式 */
.logo__path {
  fill: currentColor;
}

/* 切换按钮图标样式 */
.toggle-icon {
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

/* 用户信息区域 */
.user-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 20px;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  min-height: 80px;
}

.sidebar.collapsed .user-info {
  padding: 16px 12px;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  min-height: 100px;
}

/* 用户头像 */
.user-avatar {
  width: 40px;
  height: 40px;
  background: #e5e7eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 16px;
  flex-shrink: 0;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.sidebar.collapsed .user-avatar {
  width: 32px;
  height: 32px;
  font-size: 14px;
}

/* 用户详情 */
.user-details {
  flex: 1;
  min-width: 0;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.sidebar.collapsed .user-details {
  display: none;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-email {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 退出登录按钮 */
.logout-btn {
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.logout-btn:hover {
  background: #f3f4f6;
  color: #dc2626;
  border-color: #fecaca;
}

.sidebar.collapsed .logout-btn {
  width: 28px;
  height: 28px;
  font-size: 12px;
}

/* 调整导航区域高度，为底部用户信息留出空间 */
.sidebar-nav {
  padding: 16px 0;
  overflow: hidden;
  padding-bottom: 100px; /* 为底部用户信息留出空间 */
}

.sidebar.collapsed .sidebar-nav {
  padding-bottom: 120px; /* 收起状态下留更多空间 */
}

/* 响应式设计 */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .sidebar.mobile-open {
    transform: translateX(0);
  }

  .sidebar-toggle {
    left: 0 !important;
    top: 20px !important;
    z-index: 1003 !important;
  }
}
</style>

<style lang="scss">
/* 全局样式 - 主内容区域调整 */
.main-content {
  margin-left: 280px;
  transition: margin-left 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.main-content.sidebar-collapsed {
  margin-left: 80px;
}

@media (max-width: 768px) {
  .main-content,
  .main-content.sidebar-collapsed {
    margin-left: 0;
  }
}
</style>